class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    this.setHeaderCartIconAccessibility();

    document.addEventListener('ecShippingSelected', () => {
      requestAnimationFrame(() => this.applyShippingSelectionFromCalculator());
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  applyShippingSelectionFromCalculator() {
    if (typeof window.ecGetShippingSelection !== 'function') return;

    const current = window.ecGetShippingSelection();

    const inputGroup = this.querySelector('#ec-cep-input-group');
    const freteSelected = this.querySelector('#ec-cart-frete-selected');
    const shippingResult = this.querySelector('#ec-cart-shipping-result');
    const freteCep = this.querySelector('#ec-cart-frete-cep');
    const fretePrice = this.querySelector('#ec-cart-frete-price');
    const cepInput = this.querySelector('#ec-cep-input');
    const shippingLine = this.querySelector('#ec-cart-footer-shipping-line');
    const shippingValue = this.querySelector('#ec-cart-shipping-value');

    const cepValue = ((cepInput && cepInput.value) || (current && current.cep) || '').toString().trim();
    const priceValue = Number(current && current.price);
    const hasValidSelection = Boolean(cepValue) && Number.isFinite(priceValue) && priceValue >= 0;

    if (!hasValidSelection) {
      if (shippingLine) {
        shippingLine.style.display = 'none';
      }
      if (shippingValue) {
        shippingValue.textContent = '';
      }
      if (inputGroup) {
        inputGroup.style.display = 'flex';
      }
      if (freteSelected) {
        freteSelected.style.display = 'none';
      }
      return;
    }

    if (inputGroup && freteSelected) {
      inputGroup.style.display = 'none';
      freteSelected.style.display = 'flex';
    }
    if (shippingResult) {
      shippingResult.style.display = 'none';
    }
    if (freteCep && cepValue) {
      freteCep.textContent = `CEP ${cepValue}`;
    }
    const priceText = priceValue === 0 ? 'Grátis' : `R$ ${priceValue.toFixed(2).replace('.', ',')}`;
    if (fretePrice) {
      fretePrice.textContent = priceText;
    }
    if (shippingLine && shippingValue) {
      shippingLine.style.display = 'flex';
      shippingValue.textContent = priceText;
    }
  }

  refreshShippingDisplayFromCartTotal() {
    if (typeof window.ecGetShippingSelection !== 'function') return;

    const current = window.ecGetShippingSelection();
    const inputGroup = this.querySelector('#ec-cep-input-group');
    const freteSelected = this.querySelector('#ec-cart-frete-selected');
    const shippingLine = this.querySelector('#ec-cart-footer-shipping-line');
    const shippingValue = this.querySelector('#ec-cart-shipping-value');
    const fretePrice = this.querySelector('#ec-cart-frete-price');
    const cepInput = this.querySelector('#ec-cep-input');

    const cepValue = ((cepInput && cepInput.value) || (current && current.cep) || '').toString().trim();
    const basePrice = Number(current && current.price);
    const hasValidSelection = Boolean(cepValue) && Number.isFinite(basePrice) && basePrice >= 0;

    if (!hasValidSelection) {
      if (shippingLine) {
        shippingLine.style.display = 'none';
      }
      if (shippingValue) {
        shippingValue.textContent = '';
      }
      if (fretePrice) {
        fretePrice.textContent = '';
      }
      if (inputGroup) {
        inputGroup.style.display = 'flex';
      }
      if (freteSelected) {
        freteSelected.style.display = 'none';
      }
      return;
    }

    fetch(window.Shopify.routes.root + 'cart.js')
    .then((res) => res.json())
    .then((cart) => {
      const barEl = this.querySelector('#ec-free-shipping-bar') || document.getElementById('ec-free-shipping-bar');
      const thresholdCents = barEl ? parseInt(barEl.getAttribute('data-threshold-cents'), 10) : 20000;
      const isFree = cart.total_price >= thresholdCents;

      const priceToDisplay = isFree ? 0 : basePrice;
      const priceText = priceToDisplay === 0 ? 'Grátis' : `R$ ${priceToDisplay.toFixed(2).replace('.', ',')}`;

      if (fretePrice) {
        fretePrice.textContent = priceText;
      }
      if (shippingLine && shippingValue) {
        shippingLine.style.display = 'flex';
        shippingValue.textContent = priceText;
      }
    })
    .catch(() => {});
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    requestAnimationFrame(() => {
      this.applyShippingSelectionFromCalculator();
      this.refreshShippingDisplayFromCartTotal();
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) {
      return;
    }

    if (window.ecShippingCalculatorState && typeof window.ecShippingCalculatorState.save === 'function') {
      window.ecShippingCalculatorState.save();
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    
    if (parsedState.id) {
      this.productId = parsedState.id;
    }
    
    this.classList.remove('is-empty');
    
    this.getSectionsToRender().forEach((section) => {
      const sectionHTML = parsedState.sections[section.id];
      if (!sectionHTML) return;
      
      let sectionElement = null;
      
      if (section.selector) {
        sectionElement = document.querySelector(section.selector);
      } else {
        sectionElement = document.getElementById(section.id);
      }

      if (!sectionElement) return;
      
      try {
        const parsedHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
        if (parsedHTML) {
          sectionElement.innerHTML = parsedHTML;
        }
      } catch (error) {
        console.error('Erro ao renderizar seção do carrinho:', error);
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();

      if (window.ecShippingCalculatorState && typeof window.ecShippingCalculatorState.restore === 'function') {
        window.ecShippingCalculatorState.restore();
      }

      this.applyShippingSelectionFromCalculator();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const element = parsed.querySelector(selector);
      if (element) {
        return element.innerHTML;
      }
      
      const cartDrawer = parsed.querySelector('cart-drawer');
      if (cartDrawer) {
        return cartDrawer.innerHTML;
      }
      
      const cartDrawerDiv = parsed.querySelector('#CartDrawer');
      if (cartDrawerDiv) {
        return cartDrawerDiv.innerHTML;
      }
      
      return html;
    } catch (error) {
      console.error('Erro ao parsear HTML do carrinho:', error);
      return html;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
